/**
 * Banco de preguntas reutilizables (`/api/preguntas`).
 *
 * El banco lo forman las preguntas SIN instrumento (`id_instrumento` null); de
 * ahí el `?banco=1` del listado. Una pregunta del banco se asigna a indicadores
 * por el pivote M:N `indicador_pregunta`, que es catálogo y NO la calificación:
 * la pregunta que califica un indicador es la que tiene `id_indicador` dentro de
 * su propio instrumento.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http } from '@/services/http'
import type { HttpResult } from '@/services/http'
import type Pregunta from '@/interfaces/Preguntas'

const BASE = '/api/preguntas'

/**
 * Lista banco preguntas · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Pregunta[]
 */
export async function listarBancoService(): Promise<Pregunta[]> {
  const result = await http.get<Pregunta[]>(`${BASE}?banco=1`)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Crea banco preguntas · petición `POST`.
 *
 * @returns HttpResult<Pregunta>
 */
export async function crearPreguntaBancoService(
  payload: { pregunta: string; tipo: string; id_grupo_interes?: number | null; opciones?: string[] }
): Promise<HttpResult<Pregunta>> {
  return http.post<Pregunta>(BASE, payload)
}

/**
 * Actualiza banco preguntas · petición `PUT`.
 *
 * @returns HttpResult<Pregunta>
 */
export async function actualizarPreguntaBancoService(
  id: number,
  payload: { pregunta?: string; tipo?: string; id_grupo_interes?: number | null; opciones?: string[] }
): Promise<HttpResult<Pregunta>> {
  return http.put<Pregunta>(`${BASE}/${id}`, payload)
}

/**
 * Elimina banco preguntas · petición `DELETE`.
 *
 * @returns HttpResult<unknown>
 */
export async function eliminarPreguntaBancoService(id: number): Promise<HttpResult<unknown>> {
  return http.delete(`${BASE}/${id}`)
}

/**
 * Asigna banco preguntas · petición `POST`.
 *
 * @returns HttpResult<Pregunta>
 */
export async function asignarAIndicadoresService(
  id: number,
  indicadores: number[]
): Promise<HttpResult<Pregunta>> {
  return http.post<Pregunta>(`${BASE}/${id}/indicadores`, { indicadores })
}

/**
 * Elimina banco preguntas · petición `DELETE`.
 *
 * @returns HttpResult<{ message: string }>
 */
export async function desasignarDeIndicadorService(
  id: number,
  idIndicador: number
): Promise<HttpResult<{ message: string }>> {
  return http.delete(`${BASE}/${id}/indicadores/${idIndicador}`)
}

/**
 * Consulta banco preguntas · `GET /api/indicadores`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Pregunta[]
 */
export async function cargarPreguntasDeIndicadorService(idIndicador: number): Promise<Pregunta[]> {
  const result = await http.get<Pregunta[]>(`/api/indicadores/${idIndicador}/preguntas`)
  return result.ok ? (result.data ?? []) : []
}
